import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { TRANSFER_TYPE_VALUE } from '@sharedModule/constants/transfer-type.constant';

/**
 * Builds the controls for the conditional transfer-detail groups required by
 * `addBeneficiary` / `createSenderTransaction`. Only `bene_bank_details` is a
 * nested object on the backend; collection-point and mobile-transfer fields are
 * flat. Spread the result into a parent FormGroup.
 */
export const getDeliveryDetailsControls = (): Record<
  string,
  AbstractControl
> => ({
  bene_bank_details: new FormGroup({
    bank_account_number: new FormControl(''),
    bank_branch_id: new FormControl(''),
    bank: new FormControl(''),
    bank_state: new FormControl(''),
    bank_city: new FormControl(''),
    bank_branch: new FormControl(''),
    bank_branch_code: new FormControl(''),
    bank_account_type: new FormControl(''),
    bank_account_name: new FormControl(''),
    bank_ifsc_code: new FormControl(''),
    bank_additional_details: new FormControl(''),
  }),
  collection_point_id: new FormControl(''),
  collection_point_name: new FormControl(''),
  collection_point_code: new FormControl(''),
  collection_point_proc_bank: new FormControl(''),
  collection_point_address: new FormControl(''),
  collection_point_city: new FormControl(''),
  collection_point_state: new FormControl(''),
  collection_point_tel: new FormControl(''),
  mobile_transfer_network_id: new FormControl(''),
  mobile_transfer_number: new FormControl(''),
  mobile_transfer_network: new FormControl(''),
});

const COLLECTION_POINT_KEYS = [
  'collection_point_id',
  'collection_point_name',
  'collection_point_code',
  'collection_point_proc_bank',
  'collection_point_address',
  'collection_point_city',
  'collection_point_state',
  'collection_point_tel',
];

const MOBILE_TRANSFER_KEYS = [
  'mobile_transfer_network_id',
  'mobile_transfer_number',
  'mobile_transfer_network',
];

/**
 * Removes the transfer-detail fields that don't apply to the chosen
 * `transfer_type_po` so the payload matches the backend contract exactly.
 * Mutates a shallow clone, never the source.
 */
export const pruneDeliveryDetails = (
  transferType: string,
  value: Record<string, unknown>
): Record<string, unknown> => {
  const result = { ...value };
  const dropBank = (): void => {
    delete result['bene_bank_details'];
  };
  const dropCollectionPoint = (): void =>
    COLLECTION_POINT_KEYS.forEach(key => delete result[key]);
  const dropMobile = (): void =>
    MOBILE_TRANSFER_KEYS.forEach(key => delete result[key]);

  switch (transferType) {
    case TRANSFER_TYPE_VALUE.bank:
      dropCollectionPoint();
      dropMobile();
      break;
    case TRANSFER_TYPE_VALUE.cashPickup:
      dropBank();
      dropMobile();
      break;
    case TRANSFER_TYPE_VALUE.mobile:
      dropBank();
      dropCollectionPoint();
      break;
    default:
      // wallet / not selected — no delivery detail fields apply.
      dropBank();
      dropCollectionPoint();
      dropMobile();
  }
  return result;
};
